import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Play, Download, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { display: 'flex', gap: 12, marginBottom: '1.5rem', alignItems: 'center' },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' },
  chartTitle: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.1rem', marginBottom: '1rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  callout: { background: 'rgba(141,184,122,0.1)', border: '1px solid rgba(141,184,122,0.25)', borderRadius: 10, padding: '1.25rem', marginBottom: '1.5rem', fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', fontSize: '0.9rem', lineHeight: 1.6 },
  calloutTitle: { fontFamily: 'Playfair Display, serif', fontSize: '1rem', color: 'var(--primary)', marginBottom: 6 },
  spinner: { display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sortBtn: { background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 4 },
};

const channelSummary = [
  { channel: 'Push', Seedling: 42, Bloom: 35, Harvest: 28 },
  { channel: 'SMS', Seedling: 28, Bloom: 38, Harvest: 45 },
  { channel: 'Email', Seedling: 18, Bloom: 32, Harvest: 40 },
];

const fallbackData = [
  { name: 'Maria L.', tier: 'Seedling', emailScore: 0.22, smsScore: 0.35, pushScore: 0.68, bestChannel: 'Push', expectedLift: '18%' },
  { name: 'Jake T.', tier: 'Bloom', emailScore: 0.45, smsScore: 0.52, pushScore: 0.41, bestChannel: 'SMS', expectedLift: '14%' },
  { name: 'Priya S.', tier: 'Seedling', emailScore: 0.15, smsScore: 0.28, pushScore: 0.72, bestChannel: 'Push', expectedLift: '22%' },
  { name: 'Carlos R.', tier: 'Harvest', emailScore: 0.55, smsScore: 0.62, pushScore: 0.38, bestChannel: 'SMS', expectedLift: '11%' },
  { name: 'Emma W.', tier: 'Bloom', emailScore: 0.48, smsScore: 0.42, pushScore: 0.45, bestChannel: 'Email', expectedLift: '12%' },
  { name: 'Liam K.', tier: 'Seedling', emailScore: 0.12, smsScore: 0.22, pushScore: 0.65, bestChannel: 'Push', expectedLift: '20%' },
  { name: 'Aisha B.', tier: 'Bloom', emailScore: 0.38, smsScore: 0.55, pushScore: 0.48, bestChannel: 'SMS', expectedLift: '15%' },
  { name: 'Noah D.', tier: 'Harvest', emailScore: 0.52, smsScore: 0.58, pushScore: 0.32, bestChannel: 'SMS', expectedLift: '10%' },
];

export default function CampaignResponse() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('pushScore');
  const [sortDir, setSortDir] = useState('desc');

  const runModel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/models/campaign-response');
      setResults(res.data?.results || fallbackData);
      toast.success('Campaign response model complete');
    } catch {
      setResults(fallbackData);
      toast.success('Campaign response model complete (demo data)');
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...(results || [])].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <h1 style={s.h1}>Campaign Response</h1>
      <p style={s.desc}>
        Predicts response likelihood by channel (Email, SMS, Push) for each customer.
        Optimizes send strategy to maximize engagement and minimize message fatigue.
      </p>

      <div style={s.callout}>
        <div style={s.calloutTitle}><Zap size={16} style={{ marginRight: 6, verticalAlign: -2 }} />Push Outperforms for Seedlings</div>
        Seedling-tier customers respond to push notifications at 2.4x the rate of email.
        This cohort skews younger (25-34) and mobile-first — they engage with in-the-moment prompts
        far more than inbox content. Shifting Seedling campaigns to push-first could lift response rates by 18-22%.
      </div>

      <div style={s.actions}>
        <button style={s.btn} onClick={runModel} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Running...</> : <><Play size={16} /> Run Model</>}
        </button>
        {results && (
          <button style={s.btnOutline} onClick={() => exportToCSV(results, 'campaign_response')}>
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {results && (
        <>
          <div style={s.card}>
            <div style={s.chartTitle}>Response Rate by Channel + Tier (%)</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={channelSummary}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(242,237,228,0.08)" />
                <XAxis dataKey="channel" tick={{ fill: 'var(--text)', fontSize: 12, fontFamily: 'DM Sans' }} />
                <YAxis tick={{ fill: 'var(--secondary)', fontSize: 11, fontFamily: 'DM Sans' }} unit="%" />
                <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid rgba(242,237,228,0.1)', borderRadius: 8, fontFamily: 'DM Sans', color: 'var(--text)' }} />
                <Legend wrapperStyle={{ fontFamily: 'DM Sans', fontSize: 12 }} />
                <Bar dataKey="Seedling" fill="var(--secondary)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Bloom" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Harvest" fill="var(--alert)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>
                  {[['name','Customer'],['tier','Tier'],['emailScore','Email'],['smsScore','SMS'],['pushScore','Push'],['bestChannel','Best Channel'],['expectedLift','Expected Lift']].map(([k,l]) => (
                    <th key={k} style={s.th}>{l}<button style={s.sortBtn} onClick={() => toggleSort(k)}>{sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => (
                  <tr key={i}>
                    <td style={s.td}>{row.name}</td>
                    <td style={s.td}>{row.tier}</td>
                    <td style={s.td}>{(row.emailScore * 100).toFixed(0)}%</td>
                    <td style={s.td}>{(row.smsScore * 100).toFixed(0)}%</td>
                    <td style={s.td}>{(row.pushScore * 100).toFixed(0)}%</td>
                    <td style={{ ...s.td, fontWeight: 600, color: 'var(--primary)' }}>{row.bestChannel}</td>
                    <td style={s.td}>{row.expectedLift}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
